import { useCallback, useState } from "react";
import type { Device } from "@gooner/shared";
import { PAIRING_CODE_LEN } from "@gooner/shared";
import { api, ApiHttpError } from "../../lib/api";
import { normalizeTypedCode, pairingErrorMessage, pairingInputProblem } from "../lib/pairingMessage";

/**
 * The reader half of pairing: take the code a screen is showing and redeem it.
 *
 * One surface reads codes now — the big thumb-sized field on the phone remote; the
 * labelled box in the desktop screens rail went with the rail. The hook outlived its
 * second caller because it holds every rule that is easy to get subtly wrong in a copy:
 * the input is upper-cased and capped as it goes, a character outside the alphabet is
 * KEPT and answered locally (it used to be silently filtered, so `000000` emptied the
 * field and read as a blank submit — see lib/pairingMessage), and submission fires on the
 * LAST character because a fixed-length code has no ambiguity about when it is finished.
 *
 * So the rules live here and the chrome stays with each caller. This is the same instinct
 * the landing's pair modal already had when it reused AddScreenCard whole rather than
 * writing "the same four-character box" twice — it just could not reach across to a
 * surface with different bones.
 */
export function usePairingCode(onClaimed: (device: Device) => void) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  /**
   * A local deadline while somebody else is holding that screen, or null.
   *
   * ITS OWN STATE, not an error string, because it is not an error: the code was right and
   * the timing was not. Kept separate so the surface can count it down and colour it as a
   * place in a queue rather than as a mistake — and so it does not go through `e.message`,
   * which `http()` prefixes with the status code ("423: You can claim…").
   */
  const [heldUntil, setHeldUntil] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (value: string) => {
      if (busy) return;
      // Empty / short / impossible never reach the server: each gets its sentence here,
      // and the field keeps what was typed so it can be fixed (Casey 2026-10-02 midday;
      // the wrong-code wording, 2026-10-21).
      const problem = pairingInputProblem(value);
      if (problem) {
        setHeldUntil(null);
        setError(problem);
        return;
      }
      setBusy(true);
      setError(null);
      setHeldUntil(null);
      try {
        const { device } = await api.screens.claim(value);
        setCode("");
        onClaimed(device);
      } catch (e) {
        if (e instanceof ApiHttpError && e.status === 423 && e.retryAfterSec !== undefined) {
          // Converted to a local deadline on RECEIPT, so a phone whose clock disagrees
          // with the server still counts the right number of seconds down.
          setHeldUntil(Date.now() + e.retryAfterSec * 1000);
          // THE TYPED CODE STAYS. They typed it correctly; clearing the field would make
          // them do it again to be told the same thing, which punishes this person for
          // somebody else's claim.
          return;
        }
        // A 404 is the one "wrong code" answer; offline and 5xx get their own sentence,
        // never that one. The typed code stays either way.
        setError(pairingErrorMessage(e));
      } finally {
        setBusy(false);
      }
    },
    [busy, onClaimed],
  );

  const onChange = useCallback(
    (raw: string) => {
      const next = normalizeTypedCode(raw);
      setCode(next);
      setError(null);
      setHeldUntil(null);
      if (next.length === PAIRING_CODE_LEN) void submit(next);
    },
    [submit],
  );

  return { code, error, heldUntil, busy, onChange, submit };
}
